import React, { useEffect, useRef, useState } from "react";
import { compressImage, PhotoPayload } from "./imageUtil";
import { submitNetlifyForm, base64ToBlob } from "./netlifyForms";
import "./EcoBot.css";

type Msg = { role: "user" | "assistant"; content: string; img?: string };

const GREETING =
  "Hi! I'm EcoBot 🌿 Ask me anything about Everything Eco — free pickups, what we take, data destruction, rewards, you name it!";

/** Lightweight local answers, used when the AI service isn't reachable (e.g. local preview). */
function localAnswer(text: string): string {
  const t = text.toLowerCase();
  if (/(free|cost|price|charge|how much)/.test(t))
    return "Home pickups for e-waste, clothing, and household donations are 100% free! For big business or office cleanouts, we give you a free quote first. 💚";
  if (/(data|wipe|hard drive|hard-drive|secure|erase)/.test(t))
    return "For computers and phones, we can wipe or physically destroy the drives so your data is gone for good. Just let us know when you book. 🔒";
  if (/(what|take|accept|pick ?up|items|recycle)/.test(t))
    return "We pick up electronics & e-waste, clothing & textiles, furniture, household goods, and food donations — plus bulk pickups for businesses.";
  if (/(where|borough|area|location|serve|nyc|brooklyn|queens|bronx|manhattan|staten)/.test(t))
    return "We serve all five boroughs of New York City! 🗽";
  if (/(receipt|tax|deduct)/.test(t))
    return "Yes — qualifying donations can come with a tax-deductible receipt through our nonprofit partners.";
  if (/(reward|point|ecopoint|perk)/.test(t))
    return "You earn EcoPoints for every pickup that you can redeem for perks. 🎁";
  if (/(business|office|company|commercial|bulk)/.test(t))
    return "We handle bulk e-waste, office cleanouts & moves, secure data destruction, and impact reports for businesses. Tap 'Talk to a human' for a free quote!";
  if (/(schedule|book|pickup|appointment)/.test(t))
    return "Awesome! Head to the 'Schedule Pickup' page to book your free pickup — it only takes a minute. 📦";
  return "Great question! I'm not 100% sure on that one — tap 'Talk to a human' below and the Everything Eco team will get right back to you. 🌿";
}

const EcoBot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [view, setView] = useState<"chat" | "human" | "sent">("chat");

  // Earth Jr. welcomes first-time visitors (once per session)
  useEffect(() => {
    if (sessionStorage.getItem("ee-ej-hi")) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("ee-ej-hi", "1");
      setTeaser(true);
    }, 2600);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => { if (open) setTeaser(false); }, [open]);
  const [messages, setMessages] = useState<Msg[]>([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // photo attachment ("can you pick this up?")
  const [pendingImg, setPendingImg] = useState<{ payload: PhotoPayload; preview: string } | null>(null);
  const [photoSent, setPhotoSent] = useState(false);
  const lastImgRef = useRef<PhotoPayload | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const onPickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { setPendingImg(await compressImage(f)); } catch { /* unreadable file — ignore */ }
  };

  // human-handoff form
  const [hName, setHName] = useState("");
  const [hEmail, setHEmail] = useState("");
  const [hMsg, setHMsg] = useState("");
  const [escalating, setEscalating] = useState(false);
  const [escalateError, setEscalateError] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, view]);

  const sendText = async (raw: string) => {
    const text = raw.trim();
    const img = pendingImg;
    if ((!text && !img) || loading) return;
    const userMsg: Msg = {
      role: "user",
      content: text || "Can you pick this up? 📷",
      img: img?.preview,
    };
    const next: Msg[] = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setPendingImg(null);
    if (img) { lastImgRef.current = img.payload; setPhotoSent(true); }
    setLoading(true);
    try {
      const res = await fetch("/.netlify/functions/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          image: img ? { media_type: img.payload.type, data: img.payload.data } : undefined,
        }),
      });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages([
        ...next,
        {
          role: "assistant",
          content: img
            ? "I can't look at photos right now — but a real person can! Tap “🙋 Talk to a human” below and I'll send your photo straight to the Everything Eco team. 💚"
            : localAnswer(text),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };
  const send = () => sendText(input);

  const STARTERS = [
    "Can I recycle this?",
    "How do I dispose of batteries?",
    "Where do I donate furniture?",
    "What can you pick up?",
  ];

  const openHuman = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (lastUser && !hMsg) setHMsg(lastUser.content);
    setEscalateError(false);
    setView("human");
  };

  const submitHuman = async (e: React.FormEvent) => {
    e.preventDefault();
    if (escalating) return;
    setEscalating(true);
    setEscalateError(false);
    const transcript = messages.map((m) => `${m.role === "user" ? "Visitor" : "EcoBot"}: ${m.content}`).join("\n");
    try {
      // Captured by Netlify Forms (Site -> Forms -> "ecobot-human") and emailed to the team.
      const img = lastImgRef.current;
      await submitNetlifyForm(
        "ecobot-human",
        { name: hName, email: hEmail, message: hMsg, transcript },
        img ? [{ field: "photo1", blob: base64ToBlob(img.data, img.type), filename: img.name || "item.jpg" }] : []
      );
      setView("sent");
    } catch {
      setEscalateError(true);
    } finally {
      setEscalating(false);
    }
  };

  return (
    <>
      {teaser && !open && (
        <div className="ecobot-teaser">
          <button className="ecobot-teaser-main" onClick={() => { setTeaser(false); setOpen(true); }}>
            <img className="ecobot-teaser-ej" src={`${process.env.PUBLIC_URL}/earthjr-wave.png`} alt="Earth Jr. waving" />
            <span>
              <strong>Hi! I'm Earth Jr.</strong><br />
              Questions about recycling or free pickups? Tap me! 💚
            </span>
          </button>
          <button
            className="ecobot-teaser-x"
            aria-label="Dismiss"
            onClick={() => setTeaser(false)}
          >
            ✕
          </button>
        </div>
      )}
      <button
        className={`ecobot-fab${open ? " open" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Chat with EcoBot"}
      >
        {open ? "✕" : <img src={`${process.env.PUBLIC_URL}/earthjr-wave.png`} alt="" style={{ width: 44, height: "auto", marginTop: 4 }} />}
      </button>

      {open && (
        <div className="ecobot-panel" role="dialog" aria-label="EcoBot chat">
          <div className="ecobot-header">
            <div className="ecobot-avatar">
              <img src={`${process.env.PUBLIC_URL}/earthjr-stand.png`} alt="" style={{ width: 30, height: "auto", display: "block" }} />
            </div>
            <div className="ecobot-title">
              <strong>EcoBot</strong>
              <span><span className="ecobot-dot" /> Online · usually instant</span>
            </div>
            <button className="ecobot-x" onClick={() => setOpen(false)} aria-label="Close">✕</button>
          </div>

          {view === "chat" && (
            <>
              <div className="ecobot-messages" ref={scrollRef}>
                {messages.map((m, i) => (
                  <div key={i} className={`ecobot-msg ${m.role}`}>
                    {m.img && <img className="ecobot-msg-img" src={m.img} alt="Attached item" />}
                    {m.content}
                  </div>
                ))}
                {loading && (
                  <div className="ecobot-msg assistant ecobot-typing">
                    <span></span><span></span><span></span>
                  </div>
                )}
              </div>
              <div className="ecobot-foot">
                {messages.length <= 1 && !loading && (
                  <div className="ecobot-chips">
                    {STARTERS.map((q) => (
                      <button key={q} className="ecobot-chip" onClick={() => sendText(q)}>{q}</button>
                    ))}
                  </div>
                )}
                {photoSent && !loading && (
                  <button className="ecobot-photo-human" onClick={openHuman}>
                    📨 Send my photo to a real person — they'll take a look
                  </button>
                )}
                <button className="ecobot-human-link" onClick={openHuman}>🙋 Talk to a human</button>
                {pendingImg && (
                  <div className="ecobot-pending">
                    <img src={pendingImg.preview} alt="Your attached item" />
                    <span>Photo attached — ask away!</span>
                    <button type="button" onClick={() => setPendingImg(null)} aria-label="Remove photo">✕</button>
                  </div>
                )}
                <form
                  className="ecobot-input-row"
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={onPickPhoto}
                    style={{ display: "none" }}
                    aria-label="Attach a photo"
                  />
                  <button
                    type="button"
                    className="ecobot-attach"
                    onClick={() => fileRef.current?.click()}
                    aria-label="Send a photo of your item"
                    title="Send a photo — is it recyclable?"
                  >
                    📷
                  </button>
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={pendingImg ? "Can you pick this up?" : "Ask EcoBot anything…"}
                    aria-label="Message"
                  />
                  <button type="submit" disabled={(!input.trim() && !pendingImg) || loading} aria-label="Send">➤</button>
                </form>
              </div>
            </>
          )}

          {view === "human" && (
            <form className="ecobot-human" onSubmit={submitHuman}>
              <p className="ecobot-human-intro">No problem — leave your info and the Everything Eco team will reach out personally. 💚</p>
              <input value={hName} onChange={(e) => setHName(e.target.value)} placeholder="Your name" />
              <input type="email" value={hEmail} onChange={(e) => setHEmail(e.target.value)} placeholder="Your email *" required />
              <textarea value={hMsg} onChange={(e) => setHMsg(e.target.value)} placeholder="How can we help? *" rows={3} required />
              {escalateError && (
                <p className="ecobot-human-err">
                  Hmm, that didn't send. You can email us directly at <a href="mailto:everythingeconyc@gmail.com">everythingeconyc@gmail.com</a>.
                </p>
              )}
              <div className="ecobot-human-btns">
                <button type="button" className="ecobot-back" onClick={() => setView("chat")}>← Back</button>
                <button type="submit" className="ecobot-send-human" disabled={escalating}>
                  {escalating ? "Sending…" : "Send to the team"}
                </button>
              </div>
            </form>
          )}

          {view === "sent" && (
            <div className="ecobot-sent">
              <div className="ecobot-sent-check">✅</div>
              <h4>Got it — thank you!</h4>
              <p>The Everything Eco team will get back to you at <strong>{hEmail}</strong> soon. 🌿</p>
              <button className="ecobot-send-human" onClick={() => { setView("chat"); }}>Back to chat</button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default EcoBot;
